import { useEffect, useRef } from 'react';

type IntervalCallback = (...args: any[]) => any;

/**
 * Declerative interval hook.
 *
 * @example
 * const [count, setCount] = useState(0);
 *
 * useInterval(() => {
 *   setCount(currentCount => currentCount + 1);
 * }, 1000);
 *
 * @param {IntervalCallback} callback The interval function
 * @param {number | null} delay Interval value. If null the interval is effectively stopped.
 */
const useInterval = (callback: IntervalCallback, delay: number | null) => {
  const savedCallback = useRef<IntervalCallback>(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const handler = (...args: any[]) => savedCallback.current(...args);

    if (delay !== null) {
      const id = setInterval(handler, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export default useInterval;
